// V1-RESIZE · segmentResizeDistribute — PURE per-Segment resize redistribution.
//
// Extracted verbatim from SegmentsTab (V2)'s inline `handleSliderChange` and
// `handleDataValueChange` so the individual-Segment resize view (V1) reuses the
// EXACT same math instead of rebuilding it. Editing ONE Segment's size (or data
// value) redistributes the difference across the OTHER unlocked Segments so the
// wheel always sums to 100 and no Segment drops below 1.
//
// SHARED-WITH-V2 NOTE: V2/SegmentsTab currently keeps its own INLINE copy of this
// logic. A follow-up should point SegmentsTab at these functions to fully de-dupe;
// it was left untouched here to avoid colliding with the V2 panel's owner. Any
// change to the distribution rule MUST be made in BOTH places until then.

export const DATA_MULTIPLIER = 1.01;

/**
 * Percentage-mode edit: set `segmentId` to `newValue` (%), then redistribute the
 * signed difference across every OTHER unlocked Segment in proportion to its
 * current size (floor 1). data_value is re-derived from the new percentages.
 * Pure — never mutates inputs; returns a NEW array (or the same array unchanged
 * when there is no unlocked Segment to absorb the difference).
 */
export function redistributeOnValueChange(
  segments,
  segmentId,
  rawNewValue,
  dataMultiplier = DATA_MULTIPLIER
) {
  const currentSegment = segments.find((s) => s.id === segmentId);
  if (!currentSegment) return segments;

  const newValue = Number(Number(rawNewValue).toFixed(2));
  const diff = newValue - currentSegment.value;

  const unlockedSegments = segments.filter(
    (s) => !s.resize_lock && s.id !== segmentId
  );
  if (unlockedSegments.length === 0) return segments;

  const totalUnlockedValue = unlockedSegments.reduce(
    (sum, sb) => sum + sb.value,
    0
  );

  const updatedSegments = segments.map((seg) => {
    if (seg.id === segmentId) return { ...seg, value: newValue };
    if (!seg.resize_lock) {
      const proportion = seg.value / totalUnlockedValue;
      const adjustment = -diff * proportion;
      const newVal = Number(Math.max(1, seg.value + adjustment).toFixed(2));
      return { ...seg, value: newVal };
    }
    return seg;
  });

  const totalData = segments.reduce((sum, seg) => sum + (seg.data_value || 0), 0);
  return updatedSegments.map((seg) => {
    const newDataValue = (seg.value / 100) * totalData * dataMultiplier;
    return { ...seg, data_value: Math.max(1, Math.ceil(newDataValue)) };
  });
}

/**
 * gordon-0925-P1-edit-wheel-segment-size-click-redirects-to-resize-pane: the
 * single commit path for a Segment's typed/dragged size, shared by BOTH the
 * number box and the slider inside SegmentSize (the ONE control now used by
 * the Segment pane and the Resize pane alike).
 *
 * gordon-0923-P1-fe5996-segment-size-edit-reverts-on-blur: `redistributeOnValueChange`
 * no-ops (returns `segments` untouched) when there is no OTHER unlocked Segment
 * to absorb the difference into (a single-Segment wheel, or every other Segment
 * locked) -- silently dropping the edit rather than committing it. Falling back
 * to a direct single-Segment write in that case is the fix fe5996 landed for the
 * number box; folding it in here gives the slider the same guarantee for free.
 */
export function applySegmentSizeChange(segments, segmentId, clampedValue) {
  const hasOtherUnlocked = segments.some(
    (segment) => segment.id !== segmentId && !segment.resize_lock
  );
  return hasOtherUnlocked
    ? redistributeOnValueChange(segments, segmentId, clampedValue)
    : segments.map((segment) =>
        segment.id === segmentId ? { ...segment, value: clampedValue } : segment
      );
}

/**
 * Data-value-mode edit: set `segmentId`'s data_value to `typedNum`, then re-derive
 * every Segment's percentage from the new data-value totals (floor 1). Pure.
 */
export function redistributeOnDataValueChange(segments, segmentId, typedNum) {
  const updated = segments.map((s) =>
    s.id === segmentId ? { ...s, data_value: typedNum } : s
  );

  const totalData = updated.reduce((sum, seg) => sum + seg.data_value, 0);

  return updated.map((s) => {
    const pct = (s.data_value / totalData) * 100;
    return { ...s, value: Number(Math.max(1, pct).toFixed(2)) };
  });
}
